import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Fade, Popper, SxProps, Theme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { QcsButton } from './QcsButton';
import { QcsTypography } from './QcsTypography';
import { useHintDismissed } from '../../../hooks/useHintDismissed';

interface QcsCoachMarkProps {
  /** Used as both the Popper anchor (via document.getElementById) and the localStorage dismiss key */
  anchorId: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  icon?: ReactNode;
  message: ReactNode;
  dismissLabel?: string;
  /** Delay in ms before showing. Default 1500 */
  delay?: number;
  /** Auto-hide timeout in ms. Default 8000. Set 0 to disable. */
  autoHideTimeout?: number;
  onDismiss?: () => void;
  width?: number;
  zIndex?: number;
  /** External visibility gate. Default true. */
  open?: boolean;
}

const ARROW_SIZE = 8;
const ARROW_SIZE_PX = `${ARROW_SIZE}px`;

type Placement = 'top' | 'bottom' | 'left' | 'right';

const arrowPositions: Record<Placement, SxProps<Theme>> = {
  top: {
    bottom: -ARROW_SIZE,
    left: '50%',
    transform: 'translateX(-50%)',
    borderWidth: `${ARROW_SIZE_PX} ${ARROW_SIZE_PX} 0 ${ARROW_SIZE_PX}`,
    borderTopColor: 'inherit',
  },
  bottom: {
    top: -ARROW_SIZE,
    left: '50%',
    transform: 'translateX(-50%)',
    borderWidth: `0 ${ARROW_SIZE_PX} ${ARROW_SIZE_PX} ${ARROW_SIZE_PX}`,
    borderBottomColor: 'inherit',
  },
  left: {
    right: -ARROW_SIZE,
    top: '50%',
    transform: 'translateY(-50%)',
    borderWidth: `${ARROW_SIZE_PX} 0 ${ARROW_SIZE_PX} ${ARROW_SIZE_PX}`,
    borderLeftColor: 'inherit',
  },
  right: {
    left: -ARROW_SIZE,
    top: '50%',
    transform: 'translateY(-50%)',
    borderWidth: `${ARROW_SIZE_PX} ${ARROW_SIZE_PX} ${ARROW_SIZE_PX} 0`,
    borderRightColor: 'inherit',
  },
};

export const QcsCoachMark: FC<QcsCoachMarkProps> = ({
  anchorId,
  placement = 'top',
  icon,
  message,
  dismissLabel,
  delay = 1500,
  autoHideTimeout = 8000,
  onDismiss,
  width = 260,
  zIndex = 1,
  open = true,
}) => {
  const { t } = useTranslation();
  const [dismissed, dismiss] = useHintDismissed(anchorId);
  const anchorEl = document.getElementById(anchorId);
  const [visible, setVisible] = useState(false);
  const autoHideRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const fadeTimeout = prefersReducedMotion ? 0 : 300;
  const entranceDelay = prefersReducedMotion ? 0 : delay;

  const active = open && !dismissed && !!anchorEl;

  const hide = useCallback(() => {
    clearTimeout(autoHideRef.current);
    setVisible(false);
    onDismiss?.();
  }, [onDismiss]);

  const hidePermanently = useCallback(() => {
    hide();
    dismiss();
  }, [hide, dismiss]);

  // Entrance delay / reset on deactivation
  useEffect(() => {
    if (!active) return;
    const id = setTimeout(() => setVisible(true), entranceDelay);
    return () => {
      clearTimeout(id);
      setVisible(false);
    };
  }, [active, entranceDelay]);

  // Auto-hide countdown
  useEffect(() => {
    if (!visible || autoHideTimeout === 0) return;
    autoHideRef.current = setTimeout(hide, autoHideTimeout);
    return () => clearTimeout(autoHideRef.current);
  }, [visible, autoHideTimeout, hide]);

  const pauseAutoHide = () => clearTimeout(autoHideRef.current);
  const resumeAutoHide = () => {
    if (autoHideTimeout > 0) {
      autoHideRef.current = setTimeout(hide, autoHideTimeout);
    }
  };

  if (!active) return null;

  return (
    <Popper open={visible} anchorEl={anchorEl} placement={placement} sx={{ zIndex }}>
      <Fade in={visible} timeout={fadeTimeout}>
        <Box
          role="status"
          aria-live="polite"
          onMouseEnter={pauseAutoHide}
          onMouseLeave={resumeAutoHide}
          sx={{
            position: 'relative',
            width,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            boxShadow: 4,
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {icon}
            <QcsTypography variant="body2" sx={{ color: 'text.primary', pt: 0.25 }}>
              {message}
            </QcsTypography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
            <QcsButton
              variant="text"
              color="primary"
              onClick={hidePermanently}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8125rem',
                minWidth: 'auto',
                px: 1,
                py: 0.25,
              }}
            >
              {dismissLabel ?? t('common.gotIt')}
            </QcsButton>
          </Box>
          {/* Arrow */}
          <Box
            sx={{
              position: 'absolute',
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderColor: 'transparent',
              color: 'background.paper',
              filter: 'drop-shadow(0px 2px 1px rgba(0,0,0,0.1))',
              ...arrowPositions[placement],
            }}
          />
        </Box>
      </Fade>
    </Popper>
  );
};
